import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LegalLayout from "../../Layout/Legal/LegalLayout";
import {
  getLegalApp,
  getLegalAppName,
  getPolicyLanguages,
} from "../../constant/legalApps";
import "./Privacy.css";

const privacyCopy = {
  zh: {
    loading: "正在加载隐私政策…",
    appNotFound: "未找到该应用",
    languageNotSupported: "该应用暂未提供当前语言的隐私政策。",
    policyNotFound: "隐私政策暂时无法访问",
    policyNotFoundDescription: "请稍后重试，或联系应用支持团队。",
    title: "隐私政策",
  },
  en: {
    loading: "Loading privacy policy…",
    appNotFound: "App not found",
    languageNotSupported: "This app does not provide a privacy policy in this language.",
    policyNotFound: "Privacy policy unavailable",
    policyNotFoundDescription: "Please try again later or contact the app support team.",
    title: "Privacy Policy",
  },
};

export default function Privacy() {
  const { lang, slug } = useParams();

  const { i18n } = useTranslation();
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const app = getLegalApp(slug);
  const supportedLanguages = getPolicyLanguages(app, "privacy");
  const copy = privacyCopy[lang] || privacyCopy.en;

  // Sync i18n language with the language in the URL
  useEffect(() => {
    if (lang && supportedLanguages.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n, supportedLanguages]);

  useEffect(() => {
    const previousTitle = document.title;
    const appName = getLegalAppName(app, lang);

    document.title = appName ? `${copy.title} · ${appName}` : copy.title;

    return () => {
      document.title = previousTitle;
    };
  }, [app, copy.title, lang]);

  useEffect(() => {
    if (!app) {
      setIsLoading(false);
      setError("app-not-found");
      setContent("");
      return;
    }

    if (!supportedLanguages.includes(lang)) {
      setIsLoading(false);
      setError("language-not-supported");
      setContent("");
      return;
    }

    const controller = new AbortController();

    const fetchPolicy = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch the markdown file from the public folder
        const mdUrl = `/${lang}/privacy/${slug}.md`;
        const res = await fetch(mdUrl, { signal: controller.signal });

        if (!res.ok) {
          throw new Error("File not found");
        }

        const text = await res.text();

        // Sometimes fetch on not found returns the index.html page
        if (text.startsWith("<!doctype html>") || text.startsWith("<!DOCTYPE html>")) {
          throw new Error("File not found (returned HTML)");
        }

        if (controller.signal.aborted) return;

        setContent(text);
      } catch (err) {
        if (controller.signal.aborted) return;

        console.error("Privacy policy fetch failed:", err);
        setError("policy-not-found");
        setContent("");
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchPolicy();

    return () => {
      controller.abort();
    };
  }, [app, lang, slug, supportedLanguages]);

  const renderError = () => {
    if (error === "app-not-found") {
      return (
        <div className="privacy-error" role="alert">
          <h1>{copy.appNotFound}</h1>
        </div>
      );
    }

    if (error === "language-not-supported") {
      return (
        <div className="privacy-error" role="alert">
          <h1>{copy.policyNotFound}</h1>
          <p>{copy.languageNotSupported}</p>
        </div>
      );
    }

    return (
      <div className="privacy-error" role="alert">
        <h1>{copy.policyNotFound}</h1>
        <p>{copy.policyNotFoundDescription}</p>
      </div>
    );
  };

  return (
    <LegalLayout app={app} lang={lang} policyType="privacy" slug={slug}>
      <article className="privacy-document">
        <div className="privacy-content-wrapper">
          {isLoading ? (
            <p className="privacy-loading" role="status">{copy.loading}</p>
          ) : error ? (
            renderError()
          ) : (
            <div className="rm-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </article>
    </LegalLayout>
  );
}
